import {useLocalStorage} from '@mantine/hooks';
import type {OnChangeFn} from '@tanstack/react-table';
import type {
  MRT_ColumnFiltersState,
  MRT_RowSelectionState,
  MRT_SortingState,
  MRT_VisibilityState,
} from 'mantine-react-table';
import React, {useImperativeHandle, useState} from 'react';
import type {
  FormFieldFilterRequest,
  FormSimpleResponse} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  FormSubmissionScopeEnum,
  ModuleEnum,
  PermissionOperationEnum
} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {Checkbox} from 'src/design/atoms/Checkbox';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {useAppSelector} from 'src/redux/hooks';
import {useGetFormListV2Query} from 'src/redux/queries/form';
import {useGetFormDefinitionQuery} from 'src/redux/queries/formDefinition';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useFormSubmissionExport} from 'src/shared/hooks/useFormSubmissionExport';
import {FormSubmissionsFilter} from './FormSubmissionsFilter';
import type {FormSubmissionRow} from './hooks/useFormSubmissionsColumns';
import {useFormSubmissionsColumns} from './hooks/useFormSubmissionsColumns';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface FormSubmissionsTableProps {
  identifier: string;
  formDefinitionId?: number;
  titleText?: string;
  defaultSorting?: MRT_SortingState;
  defaultColumnFilters?: MRT_ColumnFiltersState;
  defaultColumnVisibility?: MRT_VisibilityState;
  actionBarPreNode?: React.ReactNode;
  enableRowSelection?: boolean;
  rowSelection?: MRT_RowSelectionState;
  onRowSelectionChange?: OnChangeFn<MRT_RowSelectionState>;
  onRowClick?: (row: FormSimpleResponse) => void;
  disableCreatedByMeFilter?: boolean;
  showFormDefinitionName?: boolean;
  fromTab?: 'user' | 'all';
}

export interface FormSubmissionsTableRef {
  refetch: () => void;
}

export const FormSubmissionsTable = React.forwardRef<
  FormSubmissionsTableRef,
  FormSubmissionsTableProps
>(
  (
    {
      identifier,
      formDefinitionId,
      titleText,
      defaultSorting,
      defaultColumnFilters,
      defaultColumnVisibility,
      actionBarPreNode,
      enableRowSelection = false,
      rowSelection = {},
      onRowSelectionChange,
      onRowClick,
      disableCreatedByMeFilter = false,
      showFormDefinitionName = false,
      fromTab = 'user',
    },
    ref
  ) => {
    const navigate = useAppNavigate();
    const [createdByMe, setCreatedByMe] = useLocalStorage({
      key: `${identifier}-createdByMe`,
      defaultValue: true,
    });
    const {session} = useAppSelector(state => state.session);
    const userId = session?.user?.id;

    // Add state for custom filters
    const [customFilters, setCustomFilters] = useState<
      FormFieldFilterRequest[]
    >([]);

    // Only fetch form definition if no custom title is provided and formDefinitionId is provided
    const {data: formDefinition} = useGetFormDefinitionQuery(
      formDefinitionId as number,
      {
        skip: !!titleText || !formDefinitionId,
      }
    );

    const displayTitle = titleText ?? formDefinition?.name ?? ' ';

    const columns = useFormSubmissionsColumns({
      showFormDefinitionName,
    });

    const tableState = useTableState({
      identifier,
      columns,
      defaultColumnFilters: defaultColumnFilters ?? [],
      defaultColumnVisibility: defaultColumnVisibility ?? {publishedAt: false},
      defaultDensity: 'xs',
      defaultPagination: {
        pageIndex: 0,
        pageSize: 20,
      },
      defaultSorting: defaultSorting ?? [{id: 'createdAt', desc: true}],
      defaultShowColumnFilters: true,
    });

    const {start, length, filters, filterFns, sorting} = tableState;

    const {data, isLoading, isError, isFetching, refetch} =
      useGetFormListV2Query(
        {
          start,
          length,
          filters,
          filterFns,
          sorting,
          formDefinitionId,
          createdBy: createdByMe ? userId : undefined,
          formFieldFilters: customFilters,
        },
        {refetchOnMountOrArgChange: true}
      );

    const {handleExport, isExporting} = useFormSubmissionExport(
      FormSubmissionScopeEnum.Submissions,
      {
        formDefinitionId,
        createdBy: createdByMe ? userId : undefined,
        formFieldFilters: customFilters,
      },
      {filters, filterFns, sorting}
    );

    useImperativeHandle(ref, () => ({
      refetch,
    }));

    const rows: FormSubmissionRow[] = data?.data ?? [];
    const totalCount = data?.totalRecords ?? 0;

    const handleCreatedByMeFilter = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      const {checked} = e.target;
      setCreatedByMe(checked);
    };

    const handleAddNew = () =>
      navigate(PathUtility.FormSubmit(formDefinitionId?.toString() ?? ''));

    function handleNavigateToForm(row: FormSubmissionRow): void {
      if (onRowClick) {
        onRowClick(row);
      } else {
        const url = PathUtility.FormSubmission(
          row.formDefinitionId.toString(),
          row.id,
          'content'
        );
        navigate(`${url}?from=${fromTab}`);
      }
    }

    const handleFiltersChange = (filters: FormFieldFilterRequest[]) => {
      setCustomFilters(filters);
    };

    const {table} = useTable({
      ...tableState,
      rowSelection,
      tableOptions: {
        enableRowSelection,
        manualSorting: true,
        manualFiltering: true,
        manualPagination: true,
        columns,
        data: rows,
        onRowSelectionChange,
        getRowId: row => String(row.id),
        renderRowActions: ({row}) => (
          <TableRowActions>
            {!row.original.pendingSync && (
              <TableRowActions.View
                onClick={() => handleNavigateToForm(row.original)}
              />
            )}
          </TableRowActions>
        ),
        renderTopToolbarCustomActions: () => (
          <TableToolbar>
            {!disableCreatedByMeFilter && (
              <TableToolbar.Filters>
                <Checkbox
                  checked={createdByMe}
                  onChange={handleCreatedByMeFilter}
                  label="Created By Me"
                />
              </TableToolbar.Filters>
            )}
            <TableToolbar.Actions>
              {actionBarPreNode}
              <PermissionGuard
                module={ModuleEnum.Forms}
                operation={PermissionOperationEnum.Export}>
                <TableToolbar.Action
                  variant="export"
                  onClick={handleExport}
                  loading={isExporting}
                />
              </PermissionGuard>
              {formDefinitionId && (
                <TableToolbar.Action variant="create" onClick={handleAddNew} />
              )}
            </TableToolbar.Actions>
          </TableToolbar>
        ),
      },
      totalCount,
      isError,
      isLoading,
      isFetching,
      onRefresh: refetch,
    });

    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Forms}>
        <PageHeader>
          <PageHeader.Title>{displayTitle}</PageHeader.Title>
        </PageHeader>
        {formDefinitionId && (
          <FormSubmissionsFilter
            formDefinitionId={formDefinitionId}
            onFiltersChange={handleFiltersChange}
          />
        )}
        {table}
      </ContentWrapper>
    );
  }
);
